/**
 * React hook for tracking page views.
 *
 * Tracks a page view in PostHog when the component mounts.
 * Use at the top of every page/screen component.
 *
 * Usage:
 *   function DashboardPage() {
 *     usePageView('dashboard');
 *     return <Dashboard />;
 *   }
 *
 * See Part 22 §128.3 of the Tech Guidelines.
 */

import { useEffect } from 'react';
import { useAnalytics } from './use-analytics';

/**
 * Tracks a typed PAGE_VIEWED event on component mount.
 *
 * @param pageName - Descriptive page name (e.g. 'dashboard', 'checkup_results')
 */
export function usePageView(pageName: string): void {
  const { track } = useAnalytics();

  useEffect(() => {
    track('PAGE_VIEWED', { page: pageName });
    // Only track on mount (pageName change is intentionally excluded
    // to avoid double-tracking during renders)
  }, [pageName]);
}
